import { motion } from "framer-motion";
import { Feather } from "lucide-react";
import BanyanMark from "@/components/BanyanMark";
import SectionHeading from "@/components/SectionHeading";

const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  {
    number: "01",
    title: "Ancient Folklore, Digital Canvas",
    text: "The Panchatantra was composed over two thousand years ago as Niti Shastra — the art of wise living. We render those timeless lessons with cinematic, high-fidelity digital brushwork built to captivate early visual learners.",
  },
  {
    number: "02",
    title: "Crafted for Generation Alpha",
    text: "Today's children grow up in hyper-visual worlds. Our books meet them there — pairing rich narrative depth with immersive color, expressive animal protagonists, and pages that feel alive.",
  },
  {
    number: "03",
    title: "Global Moral Literacy",
    text: "Wisdom knows no borders. Publishing simultaneously in six languages, Banyan Tree Press connects young readers from London to Madrid, Berlin to São Paulo — one story, many tongues.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="grain relative overflow-hidden bg-emerald-deep py-24 lg:py-32"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 500px at 88% 10%, rgba(212,175,55,0.08), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="lg:sticky lg:top-32 lg:self-start"
        >
          <div className="relative mx-auto max-w-sm">
            <div className="absolute -inset-3 border border-gold/25" aria-hidden="true" />
            <div className="relative aspect-[4/5] overflow-hidden bg-emerald">
              <img
                src="/arlo-jungle.webp"
                alt="Arlo Jungle, founder of Banyan Tree Press, seated beneath the jungle canopy among parrots, a capuchin monkey, and orchids"
                className="h-full w-full object-cover"
                loading="lazy"
                data-testid="author-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-ink/40 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute inset-4 border border-gold/40" aria-hidden="true" />
            </div>
            <div className="absolute -bottom-7 left-1/2 w-[88%] -translate-x-1/2 border border-gold/30 bg-emerald-ink px-6 py-4 shadow-lift">
              <p className="font-serif text-lg italic text-cream" data-testid="author-name">
                Arlo Jungle
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.24em] text-gold/80">
                Storyteller · Founder, Banyan Tree Press
              </p>
            </div>
          </div>
        </motion.div>

        <div>
          <SectionHeading
            dark
            testId="manifesto-heading"
            eyebrow="The Manifesto"
            title={
              <>
                The Pen Behind{" "}
                <em className="text-gold">the Canopy.</em>
              </>
            }
            sub="Arlo Jungle founded Banyan Tree Press on a single conviction: the oldest stories on Earth deserve the most modern telling. Three chapters guide every book we make."
          />

          <div className="mt-14 space-y-0">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.number}
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: EASE }}
                className="group grid gap-4 border-t border-gold/15 py-9 sm:grid-cols-[auto_1fr] sm:gap-8"
                data-testid={`manifesto-chapter-${c.number}`}
              >
                <span className="font-mono text-sm tracking-[0.2em] text-gold/70 transition-colors group-hover:text-gold">
                  {c.number}
                </span>
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-cream sm:text-3xl">
                    {c.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-base leading-relaxed text-cream/65">
                    {c.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="mt-8 flex items-start gap-4 border border-gold/25 bg-emerald/60 p-6"
          >
            <Feather className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
            <p className="font-serif text-lg italic leading-relaxed text-cream/85">
              “Give a child a fable, and you give them a compass for life — wrapped
              in a story they will beg to hear again.”
              <span className="mt-2 block font-mono text-[10px] not-italic uppercase tracking-[0.24em] text-gold/75">
                — Arlo Jungle
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
